"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { PlusCircle, Building2 } from "lucide-react";
import { OrganizationCard } from "./OrganizationCard";
import type { OrganizationsOutput, UserRoles } from "@/interfaces";
import type { PlansOutput } from "@/interfaces/plan";
import { motion } from "framer-motion";

interface OrganizationsAccordionProps {
  organizations: OrganizationsOutput[];
  onHandleNavigate: (id?: string) => void;
  onDeleteOrganization: (id: string) => Promise<void>;
  getUserRoles: (organization: OrganizationsOutput) => UserRoles;
  startSession: (organization: OrganizationsOutput) => void;
  plans: PlansOutput[];
}

export function OrganizationsAccordion({
  organizations,
  onHandleNavigate,
  onDeleteOrganization,
  getUserRoles,
  startSession,
  plans,
}: OrganizationsAccordionProps) {
  const handleDeleteClick = async (id: string) => {
    await onDeleteOrganization(id);
  };

  return (
    <div className="rounded-xl overflow-hidden shadow-md border border-gray-200">
      <Accordion
        type="multiple"
        defaultValue={["organizations"]}
        className="w-full"
      >
        <AccordionItem value="organizations" className="border-none">
          <AccordionTrigger className="text-xl font-semibold py-4 bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white hover:from-[#D96E0F] hover:to-[#e58a3d] transition-all px-6 group">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 group-hover:scale-110 transition-transform" />
              <span>Suas Organizações</span>
              <span className="ml-2 bg-white text-[#EE7B11] text-sm px-2 py-0.5 rounded-full">
                {organizations.length}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="p-0 bg-white">
            <div className="p-6">
              {organizations.length === 0 ? (
                <div className="flex flex-col items-center justify-center space-y-6 py-12 bg-gray-50 rounded-xl">
                  <div className="bg-[#EE7B11]/10 p-6 rounded-full">
                    <Building2 className="h-16 w-16 text-[#EE7B11]" />
                  </div>
                  <div className="text-center max-w-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Você ainda não tem nenhuma organização
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Crie sua primeira organização para começar a gerenciar
                      seus negócios
                    </p>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] hover:from-[#D96E0F] hover:to-[#e58a3d] text-white font-bold rounded-full shadow-lg transition-all hover:shadow-xl px-8 py-6"
                      onClick={() => onHandleNavigate()}
                    >
                      <PlusCircle className="h-6 w-6 mr-2" />
                      Criar Sua Primeira Organização
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {organizations.map((org, index) => (
                    <motion.div
                      key={org.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <OrganizationCard
                        organization={org}
                        onDeleteOrganization={handleDeleteClick}
                        isCurrentOrg={false}
                        userRole={getUserRoles(org)}
                        startSession={() => startSession(org)}
                        plans={plans}
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
